import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../lib/api';
import { Alert } from './Alert';
import { Field } from './Field';

/**
 * Appairage d'un TOTP : secret, vérification, codes de secours.
 *
 * Partagé par l'écran « Mon compte » (activation volontaire) et par l'écran
 * d'appairage obligatoire imposé aux superadmins. Une seule copie, parce que
 * c'est du parcours d'authentification : une divergence entre les deux
 * laisserait un des deux chemins produire un compte à moitié appairé.
 *
 * `onCancel` absent = appairage OBLIGATOIRE : aucun bouton pour renoncer.
 */

interface Props {
  onDone: () => void;
  onCancel?: () => void;
}

interface SetupResponse {
  secret: string;
  qrCodeDataUrl: string;
}

type Stage =
  | { name: 'start' }
  | { name: 'pairing'; setup: SetupResponse }
  | { name: 'codes'; recoveryCodes: string[] };

const monospace = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' };

/** Bouton d'apparence lien — le portail n'a pas de classe pour ça. */
const linkLike = {
  background: 'none',
  border: 'none',
  padding: 0,
  // Couleur des liens du portail : `--color-primary` (vert pâle) était
  // presque illisible sur fond blanc (recette navigateur, 06/10/2026).
  color: 'var(--color-primary-text, inherit)',
  fontWeight: 600,
  textDecoration: 'underline',
  cursor: 'pointer',
  font: 'inherit',
} as const;

export function MfaPairing({ onDone, onCancel }: Props) {
  const [stage, setStage] = useState<Stage>({ name: 'start' });
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [saved, setSaved] = useState(false);

  function fail(err: unknown, fallback: string) {
    setError(err instanceof ApiError ? err.message : fallback);
  }

  async function start() {
    setError(null);
    setPending(true);
    try {
      setStage({ name: 'pairing', setup: await api.post<SetupResponse>('/auth/mfa/setup') });
    } catch (err) {
      fail(err, 'Impossible de démarrer l’appairage.');
    } finally {
      setPending(false);
    }
  }

  async function confirm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const code = new FormData(event.currentTarget).get('code');
    setError(null);
    setPending(true);
    try {
      const { recoveryCodes } = await api.post<{ recoveryCodes: string[] }>('/auth/mfa/confirm', {
        code: String(code ?? '').trim(),
      });
      setStage({ name: 'codes', recoveryCodes });
      setSaved(false);
    } catch (err) {
      fail(err, 'Code refusé. Vérifiez l’heure de votre téléphone et réessayez.');
    } finally {
      setPending(false);
    }
  }

  function download(codes: string[]) {
    // Un fichier plutôt qu'un copier-coller : les codes de secours servent
    // justement le jour où l'on n'a plus l'appareil sous la main.
    const blob = new Blob([`Codes de secours SEmply\n\n${codes.join('\n')}\n`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'semply-codes-de-secours.txt';
    link.click();
    URL.revokeObjectURL(url);
  }

  if (stage.name === 'codes') {
    return (
      <>
        <Alert message="Double authentification activée." variant="success" />
        <p style={{ fontSize: '0.9rem', margin: '0.75rem 0 0.5rem' }}>
          <strong>Conservez ces codes de secours.</strong> Ils ne seront plus jamais affichés, et ce
          sont eux qui vous laisseront entrer si vous perdez votre téléphone.
        </p>
        <ul
          style={{
            ...monospace,
            listStyle: 'none',
            padding: '0.75rem',
            margin: '0 0 0.75rem',
            border: '1px solid var(--color-border)',
            borderRadius: '6px',
            columns: 2,
          }}
        >
          {stage.recoveryCodes.map((code) => (
            <li key={code}>{code}</li>
          ))}
        </ul>
        <button className="btn btn-secondary" type="button" onClick={() => download(stage.recoveryCodes)}>
          Télécharger les codes
        </button>
        <label style={{ display: 'block', fontSize: '0.9rem', margin: '0.75rem 0' }}>
          <input type="checkbox" checked={saved} onChange={(event) => setSaved(event.target.checked)} />{' '}
          Je les ai mis à l’abri
        </label>
        <button className="btn" type="button" disabled={!saved} onClick={onDone}>
          Continuer
        </button>
      </>
    );
  }

  if (stage.name === 'pairing') {
    return (
      <form onSubmit={(event) => void confirm(event)}>
        <Alert message={error} />
        <p style={{ fontSize: '0.9rem', margin: '0 0 0.5rem' }}>
          Scannez ce code avec votre application d’authentification, puis saisissez le code à six
          chiffres qu’elle affiche.
        </p>
        {/* Fond blanc EN DUR, et non un jeton de la charte : un code QR se lit
            par le contraste entre ses modules sombres et son fond clair, et il
            lui faut une zone de silence claire tout autour. Sous la palette
            « Sombre », `--bg-color` poserait le code sur du #14171C — un code
            que la caméra du téléphone ne décoderait plus. C'est une contrainte
            de l'optique, pas un oubli de tokenisation. */}
        <img
          src={stage.setup.qrCodeDataUrl}
          alt="Code QR d’appairage"
          style={{ display: 'block', margin: '0 0 0.5rem', background: '#fff', borderRadius: '6px' }}
        />
        <p style={{ fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
          <button type="button" style={linkLike} onClick={() => setShowSecret((value) => !value)}>
            {showSecret ? 'Masquer' : 'Impossible de scanner ?'}
          </button>
          {showSecret ? (
            <span style={{ ...monospace, display: 'block', marginTop: '0.4rem' }}>
              {stage.setup.secret}
            </span>
          ) : null}
        </p>
        <Field
          label="Code à six chiffres"
          name="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          required
        />
        <button className="btn" type="submit" disabled={pending}>
          {pending ? 'Vérification…' : 'Activer'}
        </button>
        {onCancel ? (
          <button className="btn btn-secondary" type="button" onClick={onCancel}>
            Annuler
          </button>
        ) : null}
      </form>
    );
  }

  return (
    <>
      <Alert message={error} />
      <button className="btn" type="button" disabled={pending} onClick={() => void start()}>
        {pending ? 'Préparation…' : 'Activer la double authentification'}
      </button>
      {onCancel ? (
        <button className="btn btn-secondary" type="button" onClick={onCancel}>
          Annuler
        </button>
      ) : null}
    </>
  );
}
